import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { StatusBadge } from "@/design-system/StatusBadge";
import {
  choosePoolSelection,
  readAccountRegistry,
  readPoolSelection,
  type PoolLimitsBody,
  type PoolSelectionResponse,
} from "@/shared/api";
import { money } from "@/shared/money/money";

type Pool = "PRIME" | "PULSE" | "FLASH";
const POOLS: readonly Pool[] = ["PRIME", "PULSE", "FLASH"];
const KEY = ["account-pools"] as const;

const DESCRIPTION: Readonly<Record<Pool, string>> = {
  PRIME: "Основной пул, самые устойчивые монеты",
  PULSE: "Быстрый оборот: вход по рейтингу, выход от +3%",
  FLASH: "Выведен: новых сделок нет, открытые доработают",
};

/** Each pool's slots under the platform's current version, as the strategy
 *  screen reads them: the version caps what any selection can hold. */
type Capacity = Readonly<Partial<Record<Pool, number>>>;

function useCurrentCapacity(): Capacity {
  const query = useQuery({
    queryKey: ["strategy-registry", "current-capacity"] as const,
    retry: false,
    queryFn: async () => {
      const { data } = await readAccountRegistry();
      if (data === undefined) throw new Error("registry unavailable");
      const capacity: Partial<Record<Pool, number>> = {};
      for (const version of data.versions) {
        const pool = version.strategy_id as Pool;
        if (version.current === true && POOLS.includes(pool) && version.pool != null) {
          capacity[pool] = version.pool.capacity;
        }
      }
      return capacity;
    },
  });
  return query.data ?? {};
}

const PROFILE: Readonly<Record<string, string>> = {
  CONSERVATIVE: "Консервативный",
  BALANCED: "Сбалансированный",
  ACTIVE: "Активный",
};

/** Whole USDT committed if every selected slot were filled: count × price. */
function planned(limits: PoolLimitsBody, prices: Readonly<Record<string, string>>): string {
  let cents = 0n;
  for (const pool of POOLS) {
    const [whole = "0", fraction = ""] = (prices[pool] ?? "0").split(".");
    const price = BigInt(whole) * 100n + BigInt((fraction + "00").slice(0, 2));
    cents += price * BigInt(limits[pool]);
  }
  const whole = cents / 100n;
  const rest = (cents % 100n).toString().padStart(2, "0");
  return `${whole.toString()}.${rest}`;
}

function Editor({ data, owner, capacity }: {
  readonly data: PoolSelectionResponse;
  readonly owner: boolean;
  readonly capacity: Capacity;
}): React.JSX.Element {
  const queries = useQueryClient();
  const [mode, setMode] = useState<"AUTO" | "SELECT">(data.mode);
  const [limits, setLimits] = useState<PoolLimitsBody>(data.limits);
  const save = useMutation({
    mutationFn: async () => {
      const { response } = await choosePoolSelection({ body: mode === "AUTO" ? { mode } : { mode, limits } });
      if (response?.ok !== true) throw new Error("refused");
    },
    onSuccess: async () => { await queries.invalidateQueries({ queryKey: KEY }); },
  });
  const account = data.maximum ?? 25;
  const ceiling = (pool: Pool): number => Math.min(account, capacity[pool] ?? account);
  const chosen: PoolLimitsBody = mode === "AUTO" ? { PRIME: account, PULSE: account, FLASH: account } : limits;
  const shown: PoolLimitsBody = {
    PRIME: Math.min(chosen.PRIME, ceiling("PRIME")),
    PULSE: Math.min(chosen.PULSE, ceiling("PULSE")),
    FLASH: Math.min(chosen.FLASH, ceiling("FLASH")),
  };
  const slots = POOLS.reduce((sum, pool) => sum + shown[pool], 0);
  const changed = mode !== data.mode || (mode === "SELECT" && POOLS.some((pool) => limits[pool] !== data.limits[pool]));
  const nothing = mode === "SELECT" && slots === 0;

  return (
    <div className="flex flex-col gap-4 text-body-main text-text-secondary">
      {owner && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Профиль">
          {Object.entries(data.profiles).map(([name, profile]) => (
            <Button
              key={name}
              type="button"
              variant="ghost"
              onClick={() => {
                setMode(profile.mode);
                if (profile.limits != null) setLimits(profile.limits);
              }}
            >
              {PROFILE[name] ?? name}
            </Button>
          ))}
        </div>
      )}
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          className="size-4 cursor-pointer"
          checked={mode === "AUTO"}
          disabled={!owner}
          onChange={(event) => { setMode(event.target.checked ? "AUTO" : "SELECT"); }}
        />
        <span>Все пулы (авто) — Score сам распределяет слоты между пулами</span>
      </label>
      <ul className="flex flex-col gap-3" data-testid="pool-rows">
        {POOLS.map((pool) => (
          <li key={pool} className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-text-primary">{pool}</span>
              {ceiling(pool) === 0
                ? <StatusBadge tone="muted">новых сделок нет</StatusBadge>
                : shown[pool] === 0
                  ? <StatusBadge tone="muted">выключен</StatusBadge>
                  : <span className="tabular-nums">{shown[pool]} из {ceiling(pool)} слотов · слот {money(data.slot_price[pool] ?? "0", 0)} USDT</span>}
            </div>
            <span className="text-helper-sm text-text-muted">{DESCRIPTION[pool]}</span>
            {mode === "SELECT" && owner && ceiling(pool) > 0 && (
              <input
                type="range"
                min={0}
                max={ceiling(pool)}
                value={limits[pool]}
                aria-label={`Слотов ${pool}`}
                className="w-full cursor-pointer"
                onChange={(event) => { setLimits({ ...limits, [pool]: event.target.valueAsNumber }); }}
              />
            )}
          </li>
        ))}
      </ul>
      <div className="rounded-row border border-border-subtle bg-background-base p-3" data-testid="pool-summary">
        <p className="text-text-primary">Сводка</p>
        <p>Слотов всего: {slots}. Если все будут заняты, в работе будет {money(planned(shown, data.slot_price), 0)} USDT.</p>
        <p className="text-helper-sm text-text-muted">Плечо, размер слота, резерв под сетку и лимиты риска задаёт стратегия. Настройка действует на новые циклы; открытые позиции сопровождаются до закрытия, даже если их пул выключен.</p>
      </div>
      {nothing && <p role="alert" className="text-status-warning">Включите хотя бы один пул.</p>}
      {owner && (
        <div className="flex items-center gap-3">
          <Button type="button" variant="primary" disabled={!changed || nothing || save.isPending} onClick={() => { save.mutate(); }}>
            Сохранить
          </Button>
          {save.isError && <p role="alert" className="text-status-warning">Не сохранено. Обновите страницу и попробуйте ещё раз.</p>}
          {save.isSuccess && !changed && <p role="status">Сохранено. Действует для новых циклов.</p>}
        </div>
      )}
    </div>
  );
}

/** Which pools the account runs and how many slots each may hold. */
export function PoolSelectionPanel({ owner }: { readonly owner: boolean }): React.JSX.Element {
  const capacity = useCurrentCapacity();
  const query = useQuery({
    queryKey: KEY,
    retry: false,
    queryFn: async () => {
      const result = await readPoolSelection({ cache: "no-store" });
      if (result.data === undefined) throw new Error("pool selection unavailable");
      return result.data;
    },
  });
  return (
    <Panel title="Пулы и слоты" testId="pool-selection">
      {query.isPending ? <p role="status">Загружаю…</p> : query.isError ? (
        <p role="alert" className="text-status-warning">Настройка пулов недоступна — состояние неизвестно.</p>
      ) : (
        <Editor key={`${query.data.mode}-${String(query.data.limits.PRIME)}-${String(query.data.limits.PULSE)}-${String(query.data.limits.FLASH)}`} data={query.data} owner={owner} capacity={capacity} />
      )}
    </Panel>
  );
}
